var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { BAlert } from "bootstrap-vue";
import { computed, ref, unref, watch } from "vue";
import { useJobMetricsStore } from "@/stores/jobMetricsStore";
import { worldwideCarbonIntensity, worldwidePowerUsageEffectiveness } from "./CarbonEmissions/carbonEmissionConstants";
import AwsEstimate from "./AwsEstimate.vue";
import CarbonEmissions from "./CarbonEmissions/CarbonEmissions.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'JobMetrics',
    props: {
        datasetFilesize: {
            type: Number,
            default: 0,
        },
        datasetId: {
            type: String,
            default: "",
        },
        datasetType: {
            type: String,
            default: "hda",
        },
        includeTitle: {
            type: Boolean,
            default: true,
        },
        jobId: {
            type: String,
            default: null,
        },
        powerUsageEffectiveness: {
            type: Number,
            default: worldwidePowerUsageEffectiveness,
        },
        geographicalServerLocationName: {
            type: String,
            default: "GLOBAL",
        },
        carbonIntensity: {
            type: Number,
            default: worldwideCarbonIntensity,
        },
        shouldShowAwsEstimate: {
            type: Boolean,
            default: false,
        },
        shouldShowCarbonEmissionEstimates: {
            type: Boolean,
            default: true,
        },
    },
    setup(__props) {
        const props = __props;
        const jobMetricsStore = useJobMetricsStore();
        function getJobMetrics() {
            return __awaiter(this, void 0, void 0, function* () {
                if (props.jobId) {
                    yield jobMetricsStore.fetchJobMetricsForJobId(props.jobId);
                }
                else {
                    yield jobMetricsStore.fetchJobMetricsForDatasetId(props.datasetId, props.datasetType);
                }
            });
        }
        watch(props, () => {
            getJobMetrics();
        }, { immediate: true });
        const ec2Instances = ref();
        import("./awsEc2ReferenceData.js").then((data) => (ec2Instances.value = data.ec2Instances));
        const jobMetrics = computed(() => {
            if (props.jobId) {
                return jobMetricsStore.getJobMetricsByJobId(props.jobId);
            }
            else {
                return jobMetricsStore.getJobMetricsByDatasetId(props.datasetId, props.datasetType);
            }
        });
        const jobMetricsGroupedByPluginType = computed(() => {
            const pluginGroups = {};
            for (const metric of jobMetrics.value) {
                // new group found
                if (!(metric.plugin in pluginGroups)) {
                    pluginGroups[metric.plugin] = {};
                }
                // Add metric to group
                const group = pluginGroups[metric.plugin];
                group[metric.title] = metric.value;
            }
            return pluginGroups;
        });
        const pluginsSortedByPluginType = computed(() => {
            return Object.keys(jobMetricsGroupedByPluginType.value).sort();
        });
        function getMetricByName(key) {
            var _a;
            return (_a = jobMetrics.value.find(({ name }) => name === key)) === null || _a === void 0 ? void 0 : _a.raw_value;
        }
        const jobRuntimeInSeconds = computed(() => {
            const runtime = getMetricByName("runtime_seconds");
            return runtime ? parseInt(runtime) : undefined;
        });
        const coresAllocated = computed(() => {
            const coreCount = getMetricByName("galaxy_slots");
            return coreCount ? parseInt(coreCount) : undefined;
        });
        const memoryAllocatedInMebibyte = computed(() => {
            const memoryUsage = getMetricByName("galaxy_memory_mb");
            return memoryUsage ? parseInt(memoryUsage) : undefined;
        });
        const estimatedServerInstance = computed(() => {
            const cores = unref(coresAllocated);
            if (!cores) {
                return;
            }
            const memory = unref(memoryAllocatedInMebibyte);
            const adjustedMemory = memory ? memory / 1024 : 0;
            const ec2 = unref(ec2Instances);
            if (!ec2) {
                return;
            }
            const serverInstance = ec2.find((instance) => {
                if (adjustedMemory === 0) {
                    // Exclude memory from search criteria
                    return instance.vCpuCount >= cores;
                }
                // Search by all criteria
                return instance.mem >= adjustedMemory && instance.vCpuCount >= cores;
            });
            if (!serverInstance) {
                return;
            }
            const cpu = serverInstance.cpu[0];
            if (!cpu) {
                return;
            }
            return {
                name: serverInstance.name,
                cpuInfo: {
                    modelName: cpu.cpuModel,
                    totalAvailableCores: cpu.coreCount,
                    tdp: cpu.tdp,
                },
            };
        });
        return { __sfc: true, props, jobMetricsStore, getJobMetrics, ec2Instances, jobMetrics, jobMetricsGroupedByPluginType, pluginsSortedByPluginType, getMetricByName, jobRuntimeInSeconds, coresAllocated, memoryAllocatedInMebibyte, estimatedServerInstance, BAlert, AwsEstimate, CarbonEmissions };
    }
});
